import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_HOST || ''}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  }
});

const api_v2 = axios.create({
  baseURL: `${process.env.REACT_APP_API_HOST || ''}/api/v2`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isCanceledRequest = (error) => {
  return axios.isCancel(error) || error?.code === 'ERR_CANCELED';
};

// --- helper: normalize server error message for UI ---
const extractServerMessage = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data;

  if (typeof data.detail === 'string' && data.detail.trim()) return data.detail.trim();
  if (typeof data?.error?.message === 'string' && data.error.message.trim()) return data.error.message.trim();

  return '';
};

const isAuthFailureResponse = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const message = `${extractServerMessage(data) || ''} ${error?.message || ''}`.toLowerCase();

  if (status === 401 || status === 403) {
    if (
      message.includes('token_not_valid') ||
      message.includes('given token not valid') ||
      message.includes('authentication credentials were not provided') ||
      message.includes('token is invalid or expired') ||
      message.includes('not authorized') ||
      message.includes('unauthorized')
    ) {
      return true;
    }
  }

  return false;
};

const isAuthEndpoint = (url = '') => {
  // url could be relative ("auth/jwt/create") or absolute (baseURL + path)
  return (
    url.includes('auth/jwt/create') ||
    url.includes('auth/jwt/refresh') ||
    url.includes('auth/jwt/verify') ||
    url.includes('auth/users')
  );
};

const createAuthCanceledError = (message = 'Session expired. Please sign in again.') => {
  if (typeof axios.CanceledError === 'function') {
    return new axios.CanceledError(message);
  }

  const error = new Error(message);
  error.__CANCEL__ = true;
  return error;
};

let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token');

  refreshPromise = axios
    .post(`${api.defaults.baseURL}/auth/jwt/refresh/`, { refresh: refreshToken })
    .then((response) => {
      const { access, refresh } = response.data || {};
      if (!access) throw new Error('No access token in refresh response');

      localStorage.setItem('accessToken', access);

      // IMPORTANT: when ROTATE_REFRESH_TOKENS=True backend returns a new refresh token
      if (refresh) {
        localStorage.setItem('refreshToken', refresh);
      }

      return access;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// Request interceptor to add auth token
const attachAuthHeader = (config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

function rememberCurrentPageAndRedirectToLogin() {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (currentPath === '/' || currentPath.startsWith('/login')) {
    return;
  }

  sessionStorage.setItem('redirectAfterLogin', currentPath);
  window.location.assign(`/login?next=${encodeURIComponent(currentPath)}`);
}

// Build the response interceptor shared by every axios instance: on a token
// failure it refreshes once (retrying the original request), otherwise it logs
// out. Non-auth failures are enriched with the server message. `instance` is the
// axios client that produced the error, so the retry re-runs against that same
// base URL/version instead of a hard-coded one.
function makeResponseInterceptor(instance) {
  return async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const authFailure = isAuthFailureResponse(error);

    const requestUrl = originalRequest?.url || '';
    const skipRefresh =
      originalRequest?.skipAuthRefresh === true || isAuthEndpoint(requestUrl);

    if (authFailure && !skipRefresh && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const access = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return instance(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        window.dispatchEvent(
          new CustomEvent('auth:logout', { detail: { reason: 'refresh_failed' } })
        );

        rememberCurrentPageAndRedirectToLogin();

        return Promise.reject(createAuthCanceledError());
      }
    }

    if (authFailure) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      window.dispatchEvent(
        new CustomEvent('auth:logout', { detail: { reason: 'auth_failed' } })
      );

      rememberCurrentPageAndRedirectToLogin();

      return Promise.reject(createAuthCanceledError());
    }

    if (status && status !== 200) {
      const serverMsg = extractServerMessage(error?.response?.data);
      if (serverMsg) error.message = serverMsg;
    }

    return Promise.reject(error);
  };
}

// Register the shared auth-header request interceptor on every instance.
api.interceptors.request.use(attachAuthHeader, (error) => Promise.reject(error));
api_v2.interceptors.request.use(attachAuthHeader, (error) => Promise.reject(error));

// Response interceptors: identical refresh/logout/error-message handling for
// each versioned client, wired through the factory above.
api.interceptors.response.use((response) => response, makeResponseInterceptor(api));
api_v2.interceptors.response.use((response) => response, makeResponseInterceptor(api_v2));

export { api_v2, isCanceledRequest };
export default api;
